'use client'

import { Form, Step } from '@/models/Form'
import { Locale } from '@/models/Lang'
import { Plan } from '@/models/Plan'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useState,
} from 'react'

type FormcontextProviderProps = {
  locale: Locale
  form: Form
  plans: Plan[]
  slug: string
}
type FormContextProps = FormcontextProviderProps & {
  steps: Form['steps']
  currentStep: number
  setCurrentStep: Dispatch<SetStateAction<number>>
  selectedPlan?: Plan
  onSelectedPlanChanged: (plan: Plan) => void
}

const FormContext = createContext<FormContextProps | undefined>(undefined)

const usePlethoreForm = (): FormContextProps => {
  const value = useContext(FormContext)
  if (!value) {
    throw new Error(
      'usePlethoreForm must be wrapped in a <PlethoreFormProvider />'
    )
  }
  return value
}

const PlethoreFormProvider = ({
  locale,
  form,
  plans,
  slug,
  children,
}: PropsWithChildren<FormcontextProviderProps>) => {
  dayjs.locale(locale)

  const [currentStep, setCurrentStep] = useState<number>(0)
  const [steps, setSteps] = useState<Step[]>(form.steps)
  const [selectedPlan, setSelectedPlan] = useState<Plan>()

  const onSelectedPlanChanged = (plan: Plan) => {
    const newSteps: Step[] = []
    form.steps.forEach((step) => {
      if (step.type.name === 'membershipProfile') {
        for (let i = 1; i <= plan.membersLimit; i++) {
          // TODO: Localize title
          newSteps.push({
            ...step,
            title: `Member ${i}`,
            pathPrefix: [...step.pathPrefix, i.toString()],
          })
        }
      } else {
        newSteps.push(step)
      }
    })
    setSteps(newSteps)
    setSelectedPlan(plan)
  }

  return (
    <FormContext.Provider
      value={{
        locale,
        form,
        slug,
        plans,
        currentStep,
        setCurrentStep,
        steps,
        selectedPlan,
        onSelectedPlanChanged,
      }}
    >
      {children}
    </FormContext.Provider>
  )
}

export { PlethoreFormProvider, usePlethoreForm }
